import { boundarySource } from './boundary-journal.js';
// ft_stealth_revealed_attack_modifiers / ft_stealth_apply_revealed_attack_modifiers (stealth_reveal_on_action.php, owner 2026-09-15 — "once we
// attack we are revealed; that attack must complete"): the reveal runs BEFORE the action's dispatch (stealth-hide.ts revealAfterAction), so the
// attack that gives the creature away would no longer see the ended Hidden / Invisible rows' own roll_type_adv / _dis (bug 2026-09-15: hidden
// attacks rolled flat). The reveal banks those statuses here; the action's FIRST attack roll (skillRollArr) takes their stacks and spends them.
// Kept apart from stealth-hide.ts so skill-roll.ts can read it without importing the removal / dispatch modules.
import { type JanusCtx, type Row, activeStatuses, actorId, handBack, upper } from './context.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { statusItemId } from '../statuses/expiry.js';
import { ATTACK_ROLL_TYPE_IDS } from './rolls/attacks-against-cannot-gain-adv.js';
import { CT_GATE_KEYS } from './creature-type-gate.js';

type Carry = Array<{ statusItemId: string; definition: Row }>;
const carries = new WeakMap<JanusCtx, Map<string, Carry>>();

/** Bank the ended statuses for this caster's next attack roll in this use. The carry lives on the use's own ctx, so PHP's request-static
 *  memo reset ("a new action that ends nothing must not inherit a carry") has no twin here: a nested trigger frame of the same caster that
 *  ends something ADDS to the outer action's carry and never wipes it. */
export function addRevealedAttackCarry(ctx: JanusCtx, casterId: string, carry: Carry): void {
  let map = carries.get(boundarySource(ctx));
  if (!map) carries.set(boundarySource(ctx), map = new Map());
  const held = map.get(upper(casterId)) ?? [];
  map.set(upper(casterId), [...held, ...carry.filter((c) => !held.some((h) => h.statusItemId === c.statusItemId))]);
}

/** The roller's banked ADV / DIS for ONE attack roll, then spent. Only a d20 whose OWN roll type is an attack roll takes it (PHP applied it at
 *  the attack site only — a counterspell contest or a concentration save of a spell-attack spell carries that spell's roll_type as its used
 *  type and must not spend it); the stacks match its roll_type_1 and the used item's roll_type. A status back on the roller already counts
 *  live; each ended status counts once per axis. */
export function takeRevealedAttackAdv(ctx: JanusCtx, roller: Row, rollType: string, usedRollType: string): number {
  const id = actorId(roller), map = carries.get(boundarySource(ctx)), carry = id ? map?.get(id) : undefined;
  if (!carry || !ATTACK_ROLL_TYPE_IDS.has(upper(rollType))) return 0;
  const types = [...new Set([rollType, usedRollType].map(upper))].filter((t) => ATTACK_ROLL_TYPE_IDS.has(t));
  map!.delete(id);
  const live = new Set(activeStatuses(ctx, roller).map((status) => statusItemId(status)));
  let delta = 0;
  for (const { statusItemId: ended, definition } of carry) {
    if (live.has(ended)) continue;
    for (const [key, sign] of [['roll_type_adv', 1], ['roll_type_dis', -1]] as const) {
      if (!ctx.read.entries(definition, key).some((entry) => types.includes(upper(layerListEntryUuid(entry))))) continue;
      // PHP evaluates a source's creature-type gate against each target; no Hidden / Invisible definition carries one
      if (CT_GATE_KEYS.some((k) => ctx.read.has(definition, k))) handBack('stealth_reveal:carry-creature-type-gate');
      delta += sign;
    }
  }
  return delta;
}
